import { useEffect, useState } from "react";

import {
  BaseTextFieldProps,
  Button,
  createTheme,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import classNames from "classnames";
import { format as formatDate } from "date-fns";
import _ from "lodash";

import * as colors from "assets/colors";
import { appThemeOptions } from "containers/app/AppTheme";
import useTranslations from "hooks/useTranslations";
import { TViewMode } from "model/application/modal/CreateEditModal";
import { isValidDate, isValidDateString } from "utils/dateUtils";

import InputBaseLayout from "../InputBaseLayout";
import { IInputBaseLayout } from "../InputBaseLayout/InputBaseLayout";
import InputViewValue from "../InputViewValue";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export interface IInputDatePropsBase extends Omit<
  BaseTextFieldProps,
  "lang" | "error"
> {
  viewMode?: TViewMode;
  defaultValue?: Date | null;
  name: string;
  error?: string;
  onChange: (value: Date | null, name: string) => void;
  required?: boolean;
  // clearable?: boolean; //For this to work, we need to support null in the DB
  isUndetermined?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  lang?: {
    title?: string;
    tooltip?: string;
  };
  viewStacked?: boolean;
  views?: Array<"year" | "month" | "day">;
  format?: string;
  closeOnSelect?: boolean; // Close dialog immediately after selecting
}

export interface IInputDateProps
  extends
    IInputDatePropsBase,
    Omit<IInputBaseLayout, keyof IInputDatePropsBase> {}

const MuiPickersThemeOptions = {
  ...appThemeOptions,
  components: {
    ...(appThemeOptions.components || {}),
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        textPrimary: {
          padding: "0",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: "space-between",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          marginTop: "8px",
        },
      },
    },
  },
};

const MuiPickersTheme = createTheme(MuiPickersThemeOptions);

const useStyles = makeStyles({
  DatePicker: {
    margin: 0,
    position: "relative",
  },
  dateText: {
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: 500,
  },
});

const InputDate = ({
  viewMode = "CREATE",
  disabled = false,
  name,
  defaultValue = null,
  error,
  minDate,
  maxDate,
  required,
  // clearable = false,
  isUndetermined,
  onChange,
  onOpen,
  onClose,
  lang,
  viewStacked = false,
  format = "dd/MM/yyyy", // To match DEFAULT_DATE_FORMAT (but slightly different syntax, cf. casing of MM)
  views,
  closeOnSelect,
  placeholder,
  ...rest
}: IInputDateProps) => {
  const classes = useStyles();
  const rootLang = useTranslations();

  // null is explicit empty date, contrary to undefined which makes the underlying input default value today
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>(
    defaultValue ?? null,
  );

  const [isUndeterminedState, setIsUndeterminedState] =
    useState(isUndetermined);

  const value = isUndeterminedState ? "__" : selectedDate;

  const isInvalidDate =
    !_.isNil(value) && !(isValidDate(value) || isValidDateString(value));

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setIsUndeterminedState(false);
    onChange(date, name);
  };

  useEffect(() => {
    setSelectedDate(defaultValue);
  }, [defaultValue]);

  const getError = () => {
    if (error) {
      return error;
    }
    if (isInvalidDate) {
      return rootLang.components.filters.inputDateType.customMessage
        .invalidDate;
    }
  };

  const datePickerProps =
    viewMode === "CREATE"
      ? {}
      : {
          clearLabel: (
            <Button disableElevation>
              <span style={{ color: colors.GreyDark }}>Clear</span>
            </Button>
          ),
          cancelLabel: (
            <Button disableElevation>
              <span style={{ color: colors.GreyDark }}>Cancel</span>
            </Button>
          ),

          okLabel: (
            <Button color="secondary" variant="contained" disableElevation>
              Apply
            </Button>
          ),
        };

  const DatePickerComponent = (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={MuiPickersTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className={classNames(classes.DatePicker, `viewMode-${viewMode}`)}
            value={isUndeterminedState ? null : (selectedDate ?? null)}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
            views={views}
            onOpen={onOpen ? onOpen : () => {}}
            onClose={onClose ? onClose : () => {}}
            disabled={disabled}
            closeOnSelect={closeOnSelect}
            format={format}
            {...datePickerProps}
            slotProps={{
              textField: {
                id: name,
                fullWidth: true,
                size: "small",
                margin: "dense",
                required,
                error: !!getError(),
                placeholder,
                inputProps: {
                  "aria-label": "change date",
                  ...(isUndeterminedState ? { value: "__" } : {}),
                },
              },
            }}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );

  return (
    <InputBaseLayout
      {...rest}
      label={lang?.title}
      tooltip={lang?.tooltip}
      required={required}
      viewMode={viewMode}
      error={getError()}
      htmlFor={name}
      viewStacked={viewStacked}
      dataTestId="input-date"
    >
      {viewMode === "VIEW" ? (
        <InputViewValue
          value={selectedDate ? formatDate(new Date(selectedDate), format) : ""}
        />
      ) : (
        DatePickerComponent
      )}
    </InputBaseLayout>
  );
};

export default InputDate;
